import api from "../axios";

const getAccountId = () => {
  return sessionStorage.getItem("acc_id");
};

const getPatientId = () => {
  return sessionStorage.getItem("_pt_id");
};
const getLocationid = () => {
  return JSON.parse(sessionStorage.getItem("current_loc"))?.id;
};

export async function getSummary(
  date,
  billingType,
  locationId,
  download,
  summary,
  patientStatus,
  page = 1,
  pagination = true,
  pageSize,
  // SCRUM-683: A-Z alphabetical nav - jumps straight to the page containing
  // the first patient whose last name starts with this letter. Backend
  // computes that page itself (see the "page/jump_to_letter" spec message),
  // so `page` is intentionally omitted from the request when this is set.
  jumpToLetter,
  // SCRUM-629/685: search-icon click re-fetches this same list with `q`;
  // clicking a patient in the search dropdown re-fetches it with
  // `patient_id` instead (and, per the confirmed contract, patient_status
  // is left off that call - patient_id looks the patient up regardless of
  // active/inactive status).
  q,
  patientId,
  // SCRUM-987: Status Count pill filter - only patients with at least one
  // code in this status ("billable", "billed", "paid", "denied", "appealed").
  // Omitted when no pill is selected.
  billingStatus
) {
  const tenantId = getAccountId();

  let query;
  if (date) {
    query = `/billing/patient-billing/${tenantId}/billing-summary/${date}/`;
  } else {
    query = `/billing/patient-billing/${tenantId}/billing-summary/`;
  }

  const params = {
    billing_type: billingType,
    location_pk: locationId,
    download,
    summary,
    // "all" is the default/unfiltered Active-Inactive selection - omitted
    // entirely rather than sent as a literal "all" value, same as when a
    // patient_id lookup makes the status filter moot.
    ...(patientId || patientStatus === "all" ? {} : { patient_status: patientStatus }),
    ...(q ? { q } : {}),
    ...(patientId ? { patient_id: patientId } : {}),
    ...(billingStatus ? { billing_status: billingStatus } : {})
  };

  // Only add pagination params if NOT downloading a file (CSV/PDF usually handles this, or we want all data)
  // We explicitly pass pagination=false when we want the full list
  if (pagination && !download) {
    if (jumpToLetter) {
      params.jump_to_letter = jumpToLetter;
    } else if (!q && !patientId) {
      // A `q` search (Enter/search-icon) or a `patient_id` lookup (clicking
      // a result) targets a specific match, not a page of the full list -
      // sending along whatever `page` the table happened to be on before
      // the search would incorrectly scope the lookup to that page.
      params.page = page;
    }
    if (pageSize) {
      params.page_size = pageSize;
    }
  } else {
    params.pagination = "false";
  }

  const response = await api.get(query, {
    params,
  });

  return response.data;
}

export async function updateBillingStatus(billingCodeId, locationId, data) {
  const tenantId = getAccountId();
  const response = await api.patch(
    `/billing/${tenantId}/patient-billing/${locationId}/${billingCodeId}/update-status/`,
    data
  );
  return response.data;
}

export async function getAuditReport(patientId, date) {
  if (!patientId) {
    patientId = getPatientId();
  }
  const tenantId = getAccountId();
  const response = await api.get(
    `/billing/patient-billing/${tenantId}/download-billing/${patientId}/${date}/`
  );
  return response.data;
}

export async function lockBillingToggle(locationId) {
  const tenantId = getAccountId();
  const response = await api.post(
    `/billing/${tenantId}/patient-billing/${locationId}/toggle-billing-lock/`
  );
  return response.data;
}

// StaffColumnPreference API (SCRUM-730/732): per-user, per-view, per-tab
// column visibility - preferences are independent per billing_type
// ("rpm"/"ccm"/"all"), matching the RPM/CCM Billing tab selection. GET's
// `columns` is a flat list already scoped server-side to the requested
// billing_type (not an object keyed by every tab) - the full list of
// currently-visible column keys for that one tab. A missing/null `columns`
// means nothing's been saved yet for that tab. Save (`columns` + explicit
// `billing_type`) is a full replace each time, not incremental.
export async function getColumnPreferences(viewName, billingType) {
  const tenantId = getAccountId();
  const response = await api.get(`/accounts/${tenantId}/column-preferences/`, {
    params: { view_name: viewName, billing_type: billingType }
  });
  return response.data;
}

export async function saveColumnPreferences(viewName, columns, billingType) {
  const tenantId = getAccountId();
  const response = await api.post(`/accounts/${tenantId}/column-preferences/save/`, {
    view_name: viewName,
    columns,
    billing_type: billingType
  });
  return response.data;
}

// Fuzzy patient search (SCRUM-724/685/629): typeahead as the user types in
// the billing dashboard's search box. `patientStatus` narrows results to
// match the page's own Active/Inactive/All filter (SCRUM-685 AC 18-22) -
// named the same as getSummary's own `patient_status` param below since no
// ticket comment specifies the search endpoint's exact param name; verify
// with backend if this doesn't actually narrow results.
export async function searchBillingPatients(q, locationId, month, patientStatus) {
  const tenantId = getAccountId();
  const response = await api.get(
    `/billing/patient-billing/${tenantId}/billing-patient-search/`,
    { params: { q, location_pk: locationId, month, patient_status: patientStatus } }
  );
  return response.data;
}